import type { AccessErrorCode } from "./types/userDashboard"

/**
 * Result of an access control check.
 * Allows access or denies with a specific error code.
 */
export type UserAccessResult =
  | { canAccess: true }
  | { canAccess: false; reason: AccessErrorCode }

/**
 * Helper to construct allow/deny results concisely.
 */
export const UserAccessResultHelper = {
  allow(): UserAccessResult {
    return { canAccess: true }
  },
  deny(reason: AccessErrorCode = "UNAUTHORIZED"): UserAccessResult {
    return { canAccess: false, reason }
  },
}

/**
 * Pure domain policy: can the user create a user product from a catalog product?
 *
 * This only checks product-level constraints that can be evaluated synchronously.
 * It is intentionally decoupled from any subscription/billing concerns
 * so it can be shared between server and client without a round trip.
 *
 * @param product - product data needed for the check
 * @returns allow, or PRODUCT_SOON_TO_BE_DISCONTINUED denial
 */
export function canCreateUserProduct(product: {
  isDiscontinuedSoon?: boolean
}): UserAccessResult {
  if (product.isDiscontinuedSoon) {
    return UserAccessResultHelper.deny("PRODUCT_SOON_TO_BE_DISCONTINUED")
  }

  return UserAccessResultHelper.allow()
}
